import type { ArchetypeSlug } from '@/types/persona';
import type { Platform } from '@/types/common';
import type { AccountStatus } from '@/types/account';

// Every slug Ghost knows — used only to validate deep links. Pickers read
// the active niche's list from `GET /archetypes` instead.
export const ALL_ARCHETYPE_SLUGS: ArchetypeSlug[] = [
  'youth_coach',
  'baseball_parent',
  'd1_pitcher',
  'travel_ball_mom',
  'scout_evaluator',
  'hs_athlete',
  'fitness_coach',
  'home_workout_creator',
  'gym_lifestyle',
];

export const PLATFORM_FILTERS: ('all' | Platform)[] = [
  'all',
  'instagram',
  'tiktok',
  'youtube_shorts',
  'x',
];

export const PERSONAS_LIST_LIMIT = 200;
export const PERSONAS_LIST_POLL_MS = 30_000;

export const ACCOUNT_STATUS_TONE: Record<
  AccountStatus,
  'success' | 'warning' | 'failure' | 'neutral'
> = {
  unlinked: 'neutral',
  pending_validation: 'warning',
  active: 'success',
  login_failed: 'failure',
  login_blocked: 'failure',
  awaiting_code: 'warning',
  session_expired: 'warning',
  reauth_required: 'failure',
  checkpoint: 'failure',
  banned: 'failure',
  disconnected: 'neutral',
};

export const ACCOUNT_STATUS_LABEL: Record<AccountStatus, string> = {
  unlinked: 'Not connected',
  pending_validation: 'Connecting…',
  active: 'Connected',
  login_failed: 'Login failed',
  login_blocked: 'Blocked by Instagram',
  awaiting_code: 'Enter verification code',
  session_expired: 'Session expired',
  reauth_required: 'Reauthentication required',
  checkpoint: 'Reauthentication required',
  banned: 'Banned',
  disconnected: 'Disconnected',
};

export const ACCOUNT_ERROR_REASON: Record<string, string> = {
  invalid_credentials: 'Incorrect username or password',
  suspicious_login:
    'Instagram blocked the login from this server — a residential proxy is required (your password is fine)',
  checkpoint: 'Security challenge required — resolve in a browser, then reconnect',
  captcha_required:
    'Instagram is challenging the login with a CAPTCHA (bot detection) — your password is fine; use a cleaner residential/mobile proxy or sign in once in a browser to warm the account',
  two_factor: 'Instagram needs a verification code',
  rate_limited: 'Rate limited by Instagram — wait and try again',
  suspended: 'Account locked or suspended',
  session_expired: 'Session cookies expired',
  unknown: 'Login could not be completed — see run details',
};

export const ACCOUNT_VALIDATION_POLL_MS = 1_500;
